import React, { useState, useMemo, useEffect } from 'react';
import { leaderApi } from '../../../../api/leaderApi';
import { getInitials } from '../../../../utils/resourceUtils';
import '../../../../components/shared/forms.css';
import SharedSearchInput from '../../../../components/shared/SharedSearchInput/SharedSearchInput';

/**
 * KolonnenManagementModal Component
 * Modal for creating and editing kolonnen (teams), including member and vehicle assignment.
 */
export default function KolonnenManagementModal({
    isOpen,
    onClose,
    kolonne = null, // We receive the specific kolonne to edit now
    onKolonnenChange = () => { },
    allWorkers = [],
    allVehicles = []
}) {
    const [form, setForm] = useState({ code: '', name: '', members: [] });
    const [isSaving, setIsSaving] = useState(false);
    const [memberSearch, setMemberSearch] = useState('');
    const [attemptedSave, setAttemptedSave] = useState(false);

    useEffect(() => {
        if (isOpen && kolonne) {
            setForm({
                code: kolonne.code || '',
                name: kolonne.name || '',
                members: [...(kolonne.members || [])]
            });
            setMemberSearch('');
            setAttemptedSave(false);
        } else if (isOpen) {
            setForm({ code: '', name: '', members: [] });
            setMemberSearch('');
            setAttemptedSave(false);
        }
    }, [isOpen, kolonne]);

    const handleSave = async () => {
        setAttemptedSave(true);
        if (!form.code.trim() || !form.name.trim()) return;
        setIsSaving(true);
        try {
            const payload = { ...form };
            if (kolonne && kolonne.id) {
                await leaderApi.updateKolonne(kolonne.id, payload);
            } else {
                await leaderApi.createKolonne(payload);
            }
            onKolonnenChange();
            onClose();
        } catch (e) {
            console.error(e);
            alert('Fehler beim Speichern');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!kolonne || !kolonne.id) return;
        if (!window.confirm('Kolonne wirklich löschen?')) return;
        setIsSaving(true);
        try {
            await leaderApi.deleteKolonne(kolonne.id);
            onKolonnenChange();
            onClose();
        } catch (e) {
            console.error(e);
            alert('Fehler beim Löschen');
        } finally {
            setIsSaving(false);
        }
    };

    const toggleMember = (type, id) => {
        setForm(prev => {
            const exists = prev.members.find(m => m.type === type && m.resource_id === id);
            if (exists) {
                return { ...prev, members: prev.members.filter(m => !(m.type === type && m.resource_id === id)) };
            } else {
                return { ...prev, members: [...prev.members, { type, resource_id: id }] };
            }
        });
    };

    const filteredWorkers = useMemo(() => {
        if (!memberSearch.trim()) return allWorkers;
        const t = memberSearch.toLowerCase();
        return allWorkers.filter(w => {
            const fullName = (w.name || `${w.first_name || ''} ${w.last_name || ''}`).toLowerCase();
            return fullName.includes(t) || w.category_name?.toLowerCase().includes(t);
        });
    }, [allWorkers, memberSearch]);

    const filteredVehicles = useMemo(() => {
        if (!memberSearch.trim()) return allVehicles;
        const t = memberSearch.toLowerCase();
        return allVehicles.filter(v => {
            const licensePlate = (v.license_plate || '').toLowerCase();
            const internalName = (v.internal_name || '').toLowerCase();
            const type = (v.vehicle_type || v.category_name || 'pkw').toLowerCase();
            return licensePlate.includes(t) || internalName.includes(t) || type.includes(t);
        });
    }, [allVehicles, memberSearch]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
        }
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="modal-content" style={{ maxWidth: '700px', width: '90%', height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
                <div className="panel-header" style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                    <div className="panel-title">👥 Kolonnen-Management</div>
                    <button className="btn-close-panel" onClick={onClose} title="Schließen">✕</button>
                </div>

                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {/* Right Side - Editor (Now full width) */}
                    <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', background: '#fafafa', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.2rem' }}>
                                {kolonne ? 'Kolonne bearbeiten' : 'Neue Kolonne erstellen'}
                            </h3>
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>* Pflichtfeld</div>
                        </div>

                        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                            <div style={{ flex: 2 }}>
                                <label className="form-label">Bezeichnung *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder="z.B. Team Tiefbau Nord"
                                    className={`form-input ${attemptedSave && !form.name.trim() ? 'form-field-error' : ''}`}
                                />
                                {attemptedSave && !form.name.trim() && (
                                    <div className="form-error-message">Bitte Bezeichnung angeben</div>
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <label className="form-label">Kennung *</label>
                                <input
                                    type="text"
                                    value={form.code}
                                    onChange={e => setForm({ ...form, code: e.target.value })}
                                    placeholder="z.B. K1"
                                    maxLength={5}
                                    className={`form-input ${attemptedSave && !form.code.trim() ? 'form-field-error' : ''}`}
                                />
                                {attemptedSave && !form.code.trim() && (
                                    <div className="form-error-message">Bitte Kennung angeben</div>
                                )}
                            </div>
                        </div>

                        {/* Search Resources */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Mitglieder & Fahrzeuge hinzufügen</label>
                            <SharedSearchInput
                                placeholder="Mitarbeiter oder Fahrzeug suchen..."
                                value={memberSearch}
                                onChange={e => setMemberSearch(e.target.value)}
                                onClear={() => setMemberSearch('')}
                                style={{ marginBottom: '16px' }}
                            />
                        </div>

                        {/* Resource Selection Columns */}
                        <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: 0 }}>
                            {/* Workers List */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9', fontWeight: 600, fontSize: '0.9rem', color: '#475569' }}>
                                    👥 Mitarbeiter
                                </div>
                                <div style={{ overflowY: 'auto', flex: 1, padding: '8px' }}>
                                    {filteredWorkers.map(w => {
                                        const isSelected = form.members.some(m => m.type === 'worker' && m.resource_id === w.id);
                                        const displayName = w.name || `${w.first_name || ''} ${w.last_name || ''}`;
                                        const initials = getInitials(displayName);
                                        return (
                                            <div
                                                key={w.id}
                                                onClick={() => toggleMember('worker', w.id)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', padding: '8px 12px',
                                                    cursor: 'pointer', borderRadius: '6px', marginBottom: '4px',
                                                    background: isSelected ? '#eff6ff' : 'transparent',
                                                    transition: 'background 0.2s'
                                                }}
                                            >
                                                <input type="checkbox" checked={isSelected} readOnly style={{ marginRight: '12px', cursor: 'pointer' }} />
                                                <div className="res-avatar" style={{
                                                    background: w.color || '#3b82f6',
                                                    width: 28, height: 28, fontSize: '0.7rem', marginRight: '10px',
                                                    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
                                                }}>
                                                    {initials}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: isSelected ? 600 : 400 }}>{displayName}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{w.category_name}</div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Vehicles List */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9', fontWeight: 600, fontSize: '0.9rem', color: '#475569' }}>
                                    🚚 Fahrzeuge
                                </div>
                                <div style={{ overflowY: 'auto', flex: 1, padding: '8px' }}>
                                    {filteredVehicles.map(v => {
                                        const isSelected = form.members.some(m => m.type === 'vehicle' && m.resource_id === v.id);
                                        return (
                                            <div
                                                key={v.id}
                                                onClick={() => toggleMember('vehicle', v.id)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', padding: '8px 12px',
                                                    cursor: 'pointer', borderRadius: '6px', marginBottom: '4px',
                                                    background: isSelected ? '#eff6ff' : 'transparent',
                                                    transition: 'background 0.2s'
                                                }}
                                            >
                                                <input type="checkbox" checked={isSelected} readOnly style={{ marginRight: '12px', cursor: 'pointer' }} />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: isSelected ? 600 : 400 }}>
                                                        {v.internal_name || '—'} <span style={{ color: '#64748b', fontWeight: 400 }}>({v.license_plate || '—'})</span>
                                                    </div>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                        {v.vehicle_type === 'Kein Typ' ? 'ohne Typ' : (v.vehicle_type || v.category_name || 'ohne Typ')}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                            {kolonne && kolonne.id && (
                                <button
                                    onClick={handleDelete}
                                    style={{
                                        marginRight: 'auto', padding: '10px 16px', background: '#fef2f2',
                                        color: '#ef4444', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer'
                                    }}
                                    disabled={isSaving}
                                >
                                    Löschen
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                style={{
                                    padding: '10px 20px', background: '#fff', color: '#475569',
                                    border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: 600, cursor: 'pointer'
                                }}
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                style={{
                                    padding: '10px 24px', background: '#3b82f6', color: '#fff',
                                    border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer',
                                    opacity: isSaving ? 0.6 : 1
                                }}
                            >
                                {isSaving ? 'Speichert...' : kolonne ? 'Änderungen speichern' : 'Kolonne erstellen'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
